<script lang="ts">
  import { api, ApiError } from '$shared/api';
  import { addToast } from '$shared/toasts.svelte';
  import LoadingSkeleton from '$shared/LoadingSkeleton.svelte';

  interface InviteStats {
    allowed: number;
    sent: number;
    remaining: number;
    invites: Array<{ id: number; code: string; email: string; created_at: string; used_by: number | null }>;
  }

  let data = $state<InviteStats | null>(null);
  let loading = $state(true);
  let email = $state('');
  let sending = $state(false);
  let message = $state('');
  let messageType = $state<'success' | 'error'>('success');

  $effect(() => {
    loadInvites();
  });

  async function loadInvites() {
    try {
      data = await api.get<InviteStats>('/users/current/invite-stats');
    } catch {}
    loading = false;
  }

  async function handleSend(e: Event) {
    e.preventDefault();
    if (!email.trim() || sending) return;
    sending = true;
    message = '';
    try {
      const res = await api.post<{ message: string }>('/users/current/send-invite', { email: email.trim() });
      message = res.message || 'Invite sent!';
      messageType = 'success';
      email = '';
      await loadInvites();
    } catch (err) {
      message = err instanceof ApiError ? err.message : 'An error occurred';
      messageType = 'error';
    } finally {
      sending = false;
    }
  }

  function copyInviteLink(code: string) {
    const url = `${window.location.origin}/#/register?code=${code}`;
    navigator.clipboard
      .writeText(url)
      .then(() => {
        addToast('Invite link copied!', 'follow');
      })
      .catch(() => {
        addToast('Could not copy link', 'error');
      });
  }
</script>

<div class="narrow">
  <h1>Invite Friends</h1>
  {#if loading}
    <LoadingSkeleton lines={4} />
  {:else if data}
    <div class="card card--no-hover banner">
      You have <strong>{data.remaining}</strong> of <strong>{data.allowed}</strong> invites remaining
    </div>

    {#if message}<p class={messageType === 'error' ? 'msg--error' : 'msg--success'}>{message}</p>{/if}

    {#if data.remaining > 0}
      <form class="invite-form" onsubmit={handleSend}>
        <label for="invite-email" class="sr-only">Friend's email address</label>
        <input
          id="invite-email"
          class="input"
          type="email"
          bind:value={email}
          placeholder="friend@email.com"
          required
        />
        <button class="btn btn-primary" type="submit" disabled={sending}
          >{sending ? 'Sending...' : 'Send Invite'}</button
        >
      </form>
    {/if}

    {#if data.invites.length > 0}
      <h2>Sent Invites</h2>
      <table class="data-table" aria-label="Sent invites">
        <thead
          ><tr
            ><th scope="col">Email</th><th scope="col">Code</th><th scope="col">Link</th><th scope="col">Sent</th><th
              scope="col">Status</th
            ></tr
          ></thead
        >
        <tbody>
          {#each data.invites as inv}
            <tr>
              <td>{inv.email}</td>
              <td class="code">{inv.code.slice(0, 8)}...</td>
              <td><button class="btn-link-sm" onclick={() => copyInviteLink(inv.code)}>Copy Link</button></td>
              <td class="muted">{new Date(inv.created_at).toLocaleDateString()}</td>
              <td
                >{#if inv.used_by}<span class="msg--success">Used</span>{:else}<span class="muted">Pending</span
                  >{/if}</td
              >
            </tr>
          {/each}
        </tbody>
      </table>
    {/if}
  {/if}
</div>

<style>
  .narrow {
    max-width: 40rem;
    margin: 0 auto;
  }
  h1 {
    margin-bottom: 1.5rem;
  }
  h2 {
    margin: 1.5rem 0 0.75rem;
    font-size: 1.125rem;
  }
  .banner {
    text-align: center;
    padding: 1rem;
    margin-bottom: 1rem;
    font-size: 1.125rem;
  }
  .banner strong {
    color: var(--cyan);
  }
  .invite-form {
    display: flex;
    gap: 0.5rem;
    margin-bottom: 1.5rem;
  }
  .invite-form .input {
    flex: 1;
  }
  .btn-link-sm {
    background: none;
    border: none;
    color: var(--cyan);
    cursor: pointer;
    font-size: 0.8rem;
    padding: 0;
    text-decoration: underline;
  }
  .btn-link-sm:hover {
    color: var(--text);
  }
  .data-table {
    width: 100%;
    font-size: 0.875rem;
    border-collapse: collapse;
  }
  .sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  }
  .data-table th {
    text-align: left;
    padding-bottom: 0.5rem;
    color: var(--text-muted);
    font-weight: 500;
    border-bottom: 1px solid var(--border);
  }
  .data-table td {
    padding: 0.5rem 0;
    border-bottom: 1px solid rgba(42, 42, 42, 0.5);
  }
  .code {
    font-family: monospace;
    font-size: 0.75rem;
    color: var(--text-muted);
  }
  .muted {
    color: var(--text-secondary);
  }
</style>
